import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type {
  ClaireHandoffWindow,
  ClaireLiveQualityReport,
  ClaireOutcomeWindow,
} from '@borradh-workspace/api-client/types';
import { useState } from 'react';
import {
  OUTCOMES,
  type OutcomeKey,
  fixMarkers,
  formatDay,
  formatPct,
  handoffSplitText,
  outcomeBars,
} from './live-quality-format';
import type { ExplorerRequest } from './turn-explorer';

type Open = (r: ExplorerRequest) => void;

const shareOf = (w: ClaireOutcomeWindow, key: OutcomeKey) =>
  key === 'handled'
    ? w.handledShare
    : key === 'handedOff'
      ? w.handedOffShare
      : w.failingShare;

function targetText(
  key: OutcomeKey,
  target: { handled: number; handedOff: number }
) {
  if (key === 'handled') return `target about ${formatPct(target.handled)}`;
  if (key === 'handedOff') return `target about ${formatPct(target.handedOff)}`;
  return 'target as close to 0 as possible';
}

function targetAt(
  key: OutcomeKey,
  target: { handled: number; handedOff: number }
) {
  if (key === 'handled') return target.handled;
  if (key === 'handedOff') return target.handedOff;
  return 0;
}

const rangeText = (w: { from: string; to: string }) =>
  w.from === w.to
    ? formatDay(w.from)
    : `${formatDay(w.from)} – ${formatDay(w.to)}`;

const LINK =
  'text-primary rounded-sm text-left hover:underline focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none';

/**
 * Justified vs avoidable, from the live judge's handoff verdicts. Each
 * number opens the conversations behind it, like the tile itself.
 */
function HandoffSplit({
  window,
  minJudged,
  onOpen,
}: {
  window: ClaireHandoffWindow;
  minJudged: number;
  onOpen: Open;
}) {
  const text = handoffSplitText(window, minJudged);
  const open = (title: string, handoff: 'right' | 'unneeded' | 'missing') =>
    onOpen({
      title: `${title}, ${rangeText(window)}`,
      filter: {
        source: 'live',
        from: window.from,
        to: window.to,
        handoff,
      },
    });
  return (
    <div
      data-testid="handoff-split"
      className="text-muted-foreground mx-5 mb-5 flex flex-col gap-1 border-t pt-3 text-xs"
    >
      <div>
        {text.lead}
        {window.judged > 0 ? (
          <>
            {' '}
            <button
              type="button"
              className={LINK}
              onClick={() => open('Handoffs staff were needed for', 'right')}
            >
              {text.right}
            </button>
            ,{' '}
            <button
              type="button"
              className={LINK}
              onClick={() => open('Avoidable handoffs', 'unneeded')}
            >
              {text.avoidable}
            </button>
            .
          </>
        ) : null}
        {text.caveat ? ` ${text.caveat}` : ''}
      </div>
      <div>
        <button
          type="button"
          className={LINK}
          onClick={() => open('Should have been handed off', 'missing')}
        >
          {text.missing}
        </button>
        .
      </div>
      <div className="text-[11px]">
        The live judge samples 40% of conversations plus every handoff turn, so
        its totals are smaller than the Task Completion totals above.
      </div>
    </div>
  );
}

function Tile({
  outcome,
  window,
  target,
  handoffs,
  onOpen,
}: {
  outcome: (typeof OUTCOMES)[number];
  window: ClaireOutcomeWindow;
  target: { handled: number; handedOff: number };
  handoffs: { window: ClaireHandoffWindow; minJudged: number } | null;
  onOpen: Open;
}) {
  const share = shareOf(window, outcome.key);
  const count = window[outcome.key];
  return (
    <div className="flex flex-col rounded-xl border">
      <button
        type="button"
        data-testid={`outcome-${outcome.key}`}
        onClick={() =>
          onOpen({
            title: `${outcome.label}, ${rangeText(window)}`,
            filter: {
              source: 'live',
              from: window.from,
              to: window.to,
              outcome: outcome.filter,
            },
          })
        }
        className="hover:bg-muted/50 focus-visible:ring-ring rounded-xl p-5 text-left transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        <div className="text-muted-foreground text-sm">{outcome.label}</div>
        <div
          className={cn(
            'text-5xl leading-tight font-bold tabular-nums',
            outcome.text
          )}
        >
          {formatPct(share)}
        </div>
        <div className="bg-muted relative my-3 h-2 rounded-full">
          <div
            className={cn(
              'absolute inset-y-0 left-0 rounded-full',
              outcome.fill
            )}
            style={{ width: `${Math.round((share ?? 0) * 100)}%` }}
          />
          <div
            className="bg-foreground absolute -inset-y-1 w-0.5"
            style={{ left: `${targetAt(outcome.key, target) * 100}%` }}
            aria-hidden
          />
        </div>
        <div className="text-muted-foreground text-sm">
          {count} of {window.total} messages · {targetText(outcome.key, target)}
        </div>
        <div className="mt-1 text-sm">{outcome.meaning}</div>
        <div className="text-primary mt-2 text-sm">
          See these conversations →
        </div>
      </button>
      {outcome.key === 'handedOff' && handoffs ? (
        <HandoffSplit
          window={handoffs.window}
          minJudged={handoffs.minJudged}
          onOpen={onOpen}
        />
      ) : null}
    </div>
  );
}

function DailyBars({
  report,
  onOpen,
}: {
  report: ClaireLiveQualityReport;
  onOpen: Open;
}) {
  const bars = outcomeBars(report.days.slice(-14));
  const deploys = new Map(
    fixMarkers(report.fixes, report.days).map((m) => [m.day, m.label])
  );
  return (
    <div>
      <div className="relative mt-5 flex h-40 items-end gap-2 sm:gap-3">
        <div
          className="border-foreground/60 pointer-events-none absolute inset-x-0 z-10 border-t-2 border-dashed"
          style={{ bottom: `${report.outcomes.target.handled * 100}%` }}
          aria-hidden
        />
        {bars.map((b) => (
          <button
            type="button"
            key={b.day}
            title={`${b.label}: ${Math.round(b.handled)}% handled, ${Math.round(b.handedOff)}% handed to staff, ${Math.round(b.failing)}% failing (${b.total} messages)`}
            onClick={() =>
              onOpen({
                title: `Conversations on ${b.label}`,
                filter: { source: 'live', from: b.day, to: b.day },
              })
            }
            className="relative flex h-full flex-1 flex-col justify-end overflow-visible rounded-sm hover:opacity-80"
          >
            {deploys.has(b.day) ? (
              <span className="bg-foreground text-background absolute -top-5 left-1/2 -translate-x-1/2 rounded px-1 text-[10px] whitespace-nowrap">
                deploy
              </span>
            ) : null}
            {b.total ? (
              <>
                <div
                  className="bg-red-600 dark:bg-red-500"
                  style={{ height: `${b.failing}%` }}
                />
                <div
                  className="bg-slate-400 dark:bg-slate-500"
                  style={{ height: `${b.handedOff}%` }}
                />
                <div
                  className="bg-green-600 dark:bg-green-500"
                  style={{ height: `${b.handled}%` }}
                />
              </>
            ) : (
              <div className="bg-muted h-full" />
            )}
          </button>
        ))}
      </div>
      <div className="mt-1 flex gap-2 sm:gap-3">
        {bars.map((b) => (
          <div
            key={b.day}
            className="text-muted-foreground flex-1 text-center text-[11px]"
          >
            {b.label}
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs">
        {OUTCOMES.map((o) => (
          <span key={o.key} className="flex items-center gap-1.5">
            <span className={cn('size-2.5 rounded-sm', o.fill)} />
            {o.label}
          </span>
        ))}
        <span className="text-muted-foreground">
          Dashed line: 80% handled target. “deploy”: fixes went live that day (
          {[...deploys.values()].join(', ') || 'none shown'}).
        </span>
      </div>
    </div>
  );
}

/** How much Claire handles, hands to staff and fails, live. */
export function OutcomesSection({
  report,
  onOpen,
}: {
  report: ClaireLiveQualityReport;
  onOpen: Open;
}) {
  const { yesterday, last7, target, handoffs } = report.outcomes;
  const [tab, setTab] = useState<'yesterday' | 'last7'>(
    yesterday ? 'yesterday' : 'last7'
  );
  const window = tab === 'yesterday' && yesterday ? yesterday : last7;
  const handoffWindow =
    tab === 'yesterday' && yesterday && handoffs.yesterday
      ? handoffs.yesterday
      : handoffs.last7;
  return (
    <section className="flex flex-col gap-4">
      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="yesterday" disabled={!yesterday}>
            Yesterday{yesterday ? `, ${formatDay(yesterday.from)}` : ''}
          </TabsTrigger>
          <TabsTrigger value="last7">
            Last 7 days, {formatDay(last7.from)} – {formatDay(last7.to)}
          </TabsTrigger>
        </TabsList>
      </Tabs>
      {window.total === 0 ? (
        <p className="text-muted-foreground text-sm">
          No customer messages were judged in this window.
        </p>
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          {OUTCOMES.map((o) => (
            <Tile
              key={o.key}
              outcome={o}
              window={window}
              target={target}
              handoffs={{
                window: handoffWindow,
                minJudged: handoffs.minJudged,
              }}
              onOpen={onOpen}
            />
          ))}
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Day by day</CardTitle>
          <p className="text-muted-foreground text-sm">
            Share of each UK day&apos;s customer messages. Click a day to see
            its conversations.
          </p>
        </CardHeader>
        <CardContent>
          <DailyBars report={report} onOpen={onOpen} />
        </CardContent>
      </Card>
    </section>
  );
}
